import type PptxGenJS from 'pptxgenjs';

import { globeImage } from '@revelio/assets';
import { getAuthStoreUser, getEffectiveOverrides } from '@revelio/auth';
import { getPrimaryColor, plotColors } from '@revelio/replots';

import { getBrandConfig } from '../../../../../../../shared/branding/brand-config';

export const REPORT_LAYOUT_NAME = 'REPORT_PORTRAIT';

export const reportPptTheme = {
  slide: {
    widthInches: 7.5,
    heightInches: 10,
    marginInches: 0.4,
  },
  fonts: {
    titleSize: 10,
    legendSize: 8,
    axisLabelSize: 8,
    dataLabelSize: 7,
    gaugePrimaryTextSize: 40,
    gaugeSubTextSize: 12,
  },
  cover: {
    backgroundColor: '0f1d37',
    titleFontSize: 36,
    descriptionFontSize: 12,
    descriptionMaxHeightInches: 2.85,
    gapDescriptionToParametersInches: 0.35,
    parameterLabelFontSize: 11,
    parameterValueFontSize: 11,
    titleTopPercent: 0.22,
  },
} as const;

export const pptTheme = {
  colors: {
    brandPrimary: '#2d426a', // legacy fallback; slide masters use brand config
    text: '#2d426a',
    get chartPalette(): string[] {
      return plotColors.map((c) => c.replace('#', ''));
    },
    grid: 'E1E1E1',
    positive: '#70CA97',
    negative: '#E8A18A',
    get areaPrimary(): string {
      return getPrimaryColor();
    },
    get areaSecondary(): string {
      return plotColors[1] ?? '#5CA9E9';
    },
    white: '#FFFFFF',
  },
  fonts: {
    face: 'Arial',
    titleSize: 14,
    legendSize: 12,
    axisLabelSize: 11,
    dataLabelSize: 10,
    headerTitleSize: 18,
    coverTitleSize: 36,
  },
  layout: {
    slidePaddingX: 0.5,
    slidePaddingY: 1.0,
    chartWidth: 9,
    chartHeight: 4.0,
    header: {
      x: 0.5,
      y: 0.2,
      w: 9,
      h: 0.5,
    },
    coverImage: {
      x: 4.5,
      y: 3.5,
      w: 5.5,
      h: 2.125,
    },
  },
};

export enum PptxChartType {
  area = 'area',
  bar = 'bar',
  bar3d = 'bar3D',
  bubble = 'bubble',
  bubble3d = 'bubble3D',
  doughnut = 'doughnut',
  line = 'line',
  pie = 'pie',
  radar = 'radar',
  scatter = 'scatter',
}

export const defineSlideMasters = (pptx: PptxGenJS) => {
  const user = getAuthStoreUser();
  const overrides = getEffectiveOverrides();
  const clientName = overrides?.clientName ?? user?.client_name;
  const brand = getBrandConfig(clientName);
  const bgColor = brand.sidebarColor[500].replace('#', '');
  const hasPptCover = !!brand.coverImageLandscape;

  pptx.defineSlideMaster({
    title: 'TITLE_SLIDE',
    background: { color: bgColor },
    objects: [
      ...(hasPptCover
        ? [
            {
              image: {
                path: brand.coverImageLandscape,
                x: 0,
                y: 0,
                w: '100%' as unknown as number,
                h: '100%' as unknown as number,
              },
            },
          ]
        : [
            {
              image: {
                path: globeImage,
                x: pptTheme.layout.coverImage.x,
                y: pptTheme.layout.coverImage.y,
                w: pptTheme.layout.coverImage.w,
                h: pptTheme.layout.coverImage.h,
              },
            },
          ]),
      {
        placeholder: {
          options: {
            name: 'report_name',
            type: 'title',
            x: 0.5,
            y: 1.5,
            w: 9,
            h: 1.0,
            fontSize: pptTheme.fonts.coverTitleSize,
            color: brand.coverTitleColor.replace('#', ''),
            fontFace: pptTheme.fonts.face,
            align: 'left',
          },
          text: brand.displayName,
        },
      },
    ],
  });
};

const GLOBE_ASPECT_RATIO = 1364 / 544;

export const defineReportSlideMasters = (pptx: PptxGenJS) => {
  const { widthInches, heightInches } = reportPptTheme.slide;

  const user = getAuthStoreUser();
  const overrides = getEffectiveOverrides();
  const clientName = overrides?.clientName ?? user?.client_name;
  const brand = getBrandConfig(clientName);
  const reportCoverImagePath =
    brand.coverImagePortraitPpt ?? brand.coverImagePortrait;
  const hasPptCover = !!reportCoverImagePath;

  const backgroundColor = hasPptCover
    ? (
        brand.coverBackgroundColor ?? reportPptTheme.cover.backgroundColor
      ).replace('#', '')
    : reportPptTheme.cover.backgroundColor;

  const globeW = widthInches * 0.8;
  const globeH = globeW / GLOBE_ASPECT_RATIO;
  const globeX = widthInches - globeW;
  const globeY = heightInches - globeH;

  pptx.defineSlideMaster({
    title: 'REPORT_COVER_SLIDE',
    background: { color: backgroundColor },
    objects: hasPptCover
      ? [
          {
            image: {
              path: reportCoverImagePath,
              x: 0,
              y: 0,
              w: widthInches,
              h: heightInches,
            },
          },
        ]
      : [
          {
            image: {
              path: globeImage,
              x: globeX,
              y: globeY,
              w: globeW,
              h: globeH,
            },
          },
        ],
  });
};

export const getChartOptions = (
  title: string,
  pptChart: { opts?: Record<string, unknown> }
) => {
  return {
    chartColors: pptTheme.colors.chartPalette,
    x: pptTheme.layout.slidePaddingX,
    y: pptTheme.layout.slidePaddingY,
    w: pptTheme.layout.chartWidth,
    h: pptTheme.layout.chartHeight,
    title,
    showTitle: true,
    titleColor: pptTheme.colors.text,
    titleAlign: 'left',
    titleFontFace: pptTheme.fonts.face,
    titleFontSize: pptTheme.fonts.titleSize,
    legendColor: pptTheme.colors.text,
    legendFontSize: pptTheme.fonts.legendSize,
    catAxisLabelColor: pptTheme.colors.text,
    catAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
    valAxisLabelColor: pptTheme.colors.text,
    valAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
    dataLabelColor: pptTheme.colors.text,
    dataLabelFontSize: pptTheme.fonts.dataLabelSize,
    valGridLine: { color: pptTheme.colors.grid, style: 'solid' as const },
    ...(pptChart.opts || {}),
  };
};
